
import apiClient from './apiClient';

interface UploadUrlResponse {
    uploadUrl: string;
    publicUrl: string;
    fileKey: string;
}

export const uploadApi = {
    /**
     * Uploads a file directly to cloud storage (R2/S3) using a presigned URL.
     * returns the public URL of the uploaded file.
     */
    uploadFile: async (file: File, salonId: string, folder: 'employees' | 'products' | 'logos' = 'employees'): Promise<string> => {
        // 1. Get Presigned URL
        const { data } = await apiClient.post<UploadUrlResponse>('/storage/upload-url', {
            fileName: file.name,
            contentType: file.type,
            salonId: salonId,
            folder: folder
        });

        // 2. Parse the Cloudinary signature URL from backend
        // Example: cloudinary://signed-upload?api_key=...&timestamp=...&signature=...&upload_preset=...&public_id=...&cloud_name=...
        const uploadUrlStr = data.uploadUrl;
        if (!uploadUrlStr.startsWith('cloudinary://')) {
            throw new Error('Unsupported upload URL format. Expected cloudinary signature.');
        }

        const urlObj = new URL(uploadUrlStr);
        const searchParams = urlObj.searchParams;
        const cloudName = searchParams.get('cloud_name')!;
        
        // 3. Build Multipart Form Data for Cloudinary
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', searchParams.get('api_key')!);
        formData.append('timestamp', searchParams.get('timestamp')!);
        formData.append('signature', searchParams.get('signature')!);
        formData.append('upload_preset', searchParams.get('upload_preset')!);
        formData.append('public_id', searchParams.get('public_id')!);

        // 4. Upload file directly to Cloudinary using POST
        const cloudinaryApiUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
        
        const uploadResponse = await fetch(cloudinaryApiUrl, {
            method: 'POST',
            body: formData,
        });

        if (!uploadResponse.ok) {
            const errorBody = await uploadResponse.text();
            throw new Error(`Failed to upload file to Cloudinary: ${errorBody}`);
        }

        return data.publicUrl;
    }
};
